import { Component, Event, EventEmitter, h, Listen, Method, Prop, VNode, VNodeData } from "@stencil/core";
import { Table } from "../table";
import { TableCell } from "../table-cell/table-cell";
import { TableHeader } from "../table-header/table-header";
import { Column, TableRowWithId } from "../table-interfaces";

@Component({
    tag: "uic-table-column-custom",
    styleUrl: "table-column-custom.scss",
    shadow: false
})
export class TableColumnCustom implements Column {
    /** field name that is be passed to the cellTemplate function */
    @Prop() readonly field: string;
    /** label that is displayed in the column header */
    @Prop() readonly label: string;
    /** defines if the column should be a group */
    @Prop() readonly group: boolean = false;
    /** defines if the column should be sortable */
    @Prop() readonly sortable: boolean = false;
    /** field name that should be used for sorting instead of the column field */
    @Prop() readonly sortField?: string;
    /** wrap the content of this column if required, requires to set a max-width on table
     *  (e.g. using `full-width` or css prop `--uic-table-width`) or column */
    @Prop() readonly multiline: boolean = false;
    /** defines if the column should be filterable */
    @Prop() readonly filterable: boolean = false;
    /** defines the items which should be filterd */
    @Prop() readonly customFilterItems?: [];
    /** render function used to display HTML content in the cell.
     *  Keep in mind that any HTML code that could be used for XSS attacks must be escaped! */
    @Prop() readonly cellTemplate: (
        createElement: (tagName: string, data: VNodeData | null, children: VNode | Array<VNode> | null) => VNode,
        // eslint-disable-next-line
        row: any,
        field: string
    ) => VNode;

    /** callback function used to sort the column */
    @Prop() readonly customComparator?: (a: TableRowWithId, b: TableRowWithId) => number;

    /** callback function used to filter the column */
    @Prop() readonly customFilterCallback?: (filteredData: TableRowWithId[], selectedItems: []) => TableRowWithId[];

    /** Emitted when the filter value changes.*/
    @Event() filterEvent: EventEmitter;

    @Listen("uicTableFilter")
    onFilterRow(e: CustomEvent) {
        this.filterEvent.emit(e.detail);
    }
    /** make the render function public */
    @Method()
    async getColumnMethods() {
        return {
            renderHeader: this.renderHeader.bind(this),
            renderCell: this.renderCell.bind(this),
            getSortString: this.getSortString.bind(this)
        };
    }
    // eslint-disable-next-line
    private getSortString = (row: any) => {
        const sortField = this.sortField || this.field;
        if (sortField === "" || !row[sortField]) {
            return "";
        }
        return `${row[sortField]}`;
    };

    private renderHeader({ initialSortingCriterion: sortCriterion, data: rows = [] }: Table) {
        const sortData =
            sortCriterion?.sortProperty === this.sortField || sortCriterion?.sortProperty === this.field
                ? sortCriterion
                : undefined;
        return (
            <TableHeader
                label={this.label}
                sortable={this.sortable}
                sortField={this.sortField || this.field}
                field={this.field}
                group={this.group}
                testId={this.field}
                filterable={this.filterable}
                initialSortDirection={sortData?.direction ?? ""}
                filterItems={this.customFilterItems ? this.customFilterItems : (rows as [])}
                customComparator={this.customComparator}
                customFilterCallback={this.customFilterCallback}
            />
        );
    }
    // eslint-disable-next-line
    private renderCell = (row: any) => {
        const { field, group, multiline, cellTemplate } = this;
        const cell: VNode | string | null = typeof cellTemplate === "function" ? cellTemplate(h, row, field) : null;
        const empty = typeof cell === "undefined" || cell === null;
        return (
            <TableCell group={group} empty={empty} multiline={multiline} testId={field}>
                {cell}
            </TableCell>
        );
    };
}
